import {
	currentTargetRequired,
	getAttributeOrThrow,
	qsaOptional,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export function isSliderComponent(obj: HTMLElement): obj is SliderComponent {
	if (!obj) return false
	if (obj.localName !== 'slider-component') return false
	return true
}

export class SliderComponent extends UcoastEl {
	static htmlSelector = 'slider-component'
	static selectors = {
		slider: '[id^="Slider-"]',
		slide: '[id^="Slide-"]',
		currentCounter: '.slider-counter--current',
		totalCounter: '.slider-counter--total',
		prevButton: 'button[name="previous"]',
		nextButton: 'button[name="next"]',
		firstSlide: 'null',
		sliderButtons: 'null',
		sliderCountLink: 'null',
		autoplay: 'null',
		autoplayPaused: 'null',
		announcementBarSlider: 'null',
	}
	instanceSelectors = SliderComponent.selectors
	slider: HTMLElement
	sliderItems?: NodeListOf<HTMLElement>
	sliderItemsToShow?: HTMLElement[]
	sliderItemOffset?: number
	slidesPerPage?: number
	totalPages?: number
	currentPage?: number = 1
	slideScrollPosition?: number
	enableSliderLooping: boolean
	currentPageElement?: HTMLElement
	pageTotalElement?: HTMLElement
	prevButton?: HTMLButtonElement
	nextButton?: HTMLButtonElement
	deltaX: number
	lastHref?: string
	slideWidth?: number
	isDraggable: boolean
	hasNav: boolean
	hasExternalNav: boolean

	constructor() {
		super()
		this.setInstanceSelectors()
		this.slider = qsRequired(this.instanceSelectors.slider, this)
		this.sliderItems = qsaOptional(this.instanceSelectors.slide, this)
		this.enableSliderLooping = false
		this.currentPageElement = qsOptional(
			this.instanceSelectors.currentCounter,
			this
		)
		this.pageTotalElement = qsOptional(
			this.instanceSelectors.totalCounter,
			this
		)
		this.prevButton = qsOptional(this.instanceSelectors.prevButton, this)
		this.nextButton = qsOptional(this.instanceSelectors.nextButton, this)
		this.deltaX = 0
		this.isDraggable = this.hasAttribute('data-uc-draggable')
		this.hasNav = this.hasAttribute('data-uc-button-nav')
		this.hasExternalNav = this.hasAttribute('data-uc-external-nav')

		this.initPages()
		const resizeObserver = new ResizeObserver(
			(_entries: ResizeObserverEntry[]) => this.initPages()
		)
		resizeObserver.observe(this.slider)

		this.slider.addEventListener('scroll', this.update.bind(this))
		this.prevButton?.addEventListener(
			'click',
			this.onButtonClick.bind(this)
		)
		this.nextButton?.addEventListener(
			'click',
			this.onButtonClick.bind(this)
		)

		if (this.isDraggable) {
			this.initDraggableSlider()
			this.sliderItems?.forEach((item) => {
				if (!this.slideWidth) {
					this.slideWidth = item.offsetWidth
				}
				const innerAnchors = qsaOptional('a', item)
				if (innerAnchors) {
					for (let i = 0; i < innerAnchors.length; i++) {
						const anchor = innerAnchors[i]
						anchor.addEventListener(
							'click',
							this.preventAnchorClick.bind(this)
						)
					}
				}
			})
		}

		if (this.hasNav) {
			this.initButtonNavigation()
		} else if (this.hasExternalNav) {
			this.initExternalButtonNavigation()
		}

		this.addEventListener('slideChanged', ((
			event: CustomEvent<{ currentPage: number }>
		) => {
			this.updateExternalNavButtons(event.detail.currentPage)
			console.log(event.detail.currentPage)
		}) as EventListener)
	}

	initExternalButtonNavigation() {
		const buttonNavId = this.getAttribute('data-uc-external-nav')
		if (!buttonNavId) return
		const externalButtons = document.querySelectorAll<HTMLButtonElement>(
			`[data-uc-nav-id="${buttonNavId}"]`
		)

		externalButtons.forEach((button) => {
			button.addEventListener('click', () => {
				const slideIndex = parseInt(
					button.getAttribute('data-uc-slide') || '1',
					10
				)
				this.goToSlide(slideIndex)

				externalButtons.forEach((btn) =>
					btn.removeAttribute('aria-current')
				)
				button.setAttribute('aria-current', 'true')
			})
		})
	}

	initButtonNavigation() {
		const sliderButtons = qsaOptional<HTMLButtonElement>(
			'.slider-counter__link',
			this
		)

		sliderButtons?.forEach((button, index) => {
			button.addEventListener('click', () => {
				this.goToSlide(index + 1)
			})
		})
	}

	goToSlide(slideIndex: number) {
		if (!this.sliderItemOffset || !this.sliderItemsToShow) return

		this.currentPage = slideIndex
		this.slideScrollPosition =
			(this.currentPage - 1) * this.sliderItemOffset
		this.setSlidePosition(this.slideScrollPosition)
	}

	setInstanceSelectors() {
		this.instanceSelectors = SliderComponent.selectors
	}

	initPages() {
		this.sliderItemsToShow = this.sliderItems
			? Array.from(this.sliderItems).filter(
					(element) => element.clientWidth > 0
				)
			: []
		if (this.sliderItemsToShow.length < 2) return
		this.sliderItemOffset =
			this.sliderItemsToShow[1].offsetLeft -
			this.sliderItemsToShow[0].offsetLeft
		this.slidesPerPage = Math.floor(
			(this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
				this.sliderItemOffset
		)
		this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1
		this.update()
	}

	resetPages() {
		this.sliderItems = this.querySelectorAll(this.instanceSelectors.slide)
		this.initPages()
	}

	createSlideChangedEvent(): SlideChangedEvent {
		const currentPage = this.currentPage
		if (!currentPage)
			throw new Error(
				'cannot createSlideChangedEvent - currentPage is undefined'
			)
		if (!this.sliderItemsToShow)
			throw new Error(
				'cannot createSlideChangedEvent - sliderItemsToShow is undefined'
			)
		const currentElement = this.sliderItemsToShow[currentPage - 1]
		if (!currentElement)
			throw new Error(
				'cannot createSlideChangedEvent - currentElement is undefined'
			)
		return new CustomEvent('slideChanged', {
			detail: {
				currentPage,
				currentElement,
			},
		})
	}

	update() {
		// Temporarily prevents unneeded updates resulting from variant changes
		// This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
		if (!this.slider) return

		const previousPage = this.currentPage
		if (!this.sliderItemOffset || this.sliderItemOffset === 0) {
			this.currentPage = 1
		} else {
			this.currentPage =
				Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1
		}

		if (this.currentPageElement && this.pageTotalElement) {
			this.currentPageElement.textContent = `${this.currentPage}`
			this.pageTotalElement.textContent = `${this.totalPages}`
		}

		if (this.currentPage != previousPage) {
			if (this.sliderItemsToShow) {
				this.dispatchEvent(this.createSlideChangedEvent())
			} else {
				throw new Error('sliderItemsToShow is undefined')
			}
		}

		if (this.enableSliderLooping) return

		if (
			this.prevButton &&
			this.sliderItemsToShow &&
			this.sliderItemsToShow.length &&
			this.isSlideVisible(this.sliderItemsToShow[0]) &&
			this.slider.scrollLeft === 0
		) {
			this.prevButton.setAttribute('disabled', 'disabled')
		} else if (this.prevButton) {
			this.prevButton.removeAttribute('disabled')
		}

		if (
			this.nextButton &&
			this.sliderItemsToShow &&
			this.sliderItemsToShow.length > 0 &&
			this.isSlideVisible(
				this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
			)
		) {
			this.nextButton.setAttribute('disabled', 'disabled')
		} else if (this.nextButton) {
			this.nextButton.removeAttribute('disabled')
		}
	}

	updateExternalNavButtons(currentSlide: number) {
		const buttonNavId = this.getAttribute('data-uc-external-nav')
		if (!buttonNavId) return
		const externalButtons = document.querySelectorAll<HTMLButtonElement>(
			`[data-uc-nav-id="${buttonNavId}"]`
		)

		externalButtons.forEach((button) => {
			const slideIndex = parseInt(
				button.getAttribute('data-uc-slide') || '1',
				10
			)
			if (slideIndex === currentSlide) {
				button.setAttribute('aria-current', 'true')
			} else {
				button.removeAttribute('aria-current')
			}
		})
	}

	isSlideVisible(element: HTMLElement, offset = 0) {
		const lastVisibleSlide =
			this.slider.clientWidth + this.slider.scrollLeft - offset
		return (
			element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
			element.offsetLeft >= this.slider.scrollLeft
		)
	}

	onButtonClick(event: MouseEvent) {
		event.preventDefault()
		const currentTarget = currentTargetRequired(event)
		if (!(currentTarget instanceof HTMLButtonElement)) return
		if (!this.sliderItemOffset) return
		const step = currentTarget.dataset.step
			? parseInt(currentTarget.dataset.step)
			: 1
		this.slideScrollPosition =
			currentTarget.name === 'next'
				? this.slider.scrollLeft + step * this.sliderItemOffset
				: this.slider.scrollLeft - step * this.sliderItemOffset
		this.setSlidePosition(this.slideScrollPosition)
	}

	setSlidePosition(position: number) {
		this.slider.scrollTo({
			left: position,
		})
	}

	initDraggableSlider() {
		this.slider.addEventListener('mousedown', this.onMouseDown.bind(this))
	}

	getLastHref() {
		return this.lastHref
	}

	preventAnchorClick(event: Event) {
		event.preventDefault()

		const currentTarget = currentTargetRequired(event)

		if (!(currentTarget instanceof HTMLAnchorElement)) {
			console.error(
				'something is wrong - the current target here should always be an anchor element'
			)
		}
		this.lastHref = getAttributeOrThrow('href', currentTarget)
	}

	onMouseDown(event: MouseEvent) {
		const clientX = event.clientX
		//const initialScroll = this.slider.scrollLeft
		this.deltaX = 0

		const onMouseMove = (e: MouseEvent) => {
			e.preventDefault()
			const currentClientX = e.clientX
			const deltaX = clientX - currentClientX
			this.deltaX = deltaX
		}

		const onMouseUp = (event: MouseEvent) => {
			event.preventDefault()
			document.removeEventListener('mousemove', onMouseMove)
			document.removeEventListener('mouseup', onMouseUp)

			if (-10 < this.deltaX && this.deltaX < 10) {
				window.setTimeout(() => {
					if (this.getLastHref()) {
						window.location.href =
							this.getLastHref() ?? window.location.href
					}
				}, 2)
			} else if (this.deltaX < 0) {
				if (!this.slideWidth || !this.currentPage) {
					throw new Error('no slide found, cannot scroll slider')
				}
				const moveDistance =
					this.slider.scrollLeft - this.slideWidth - 5
				this.slider.scrollLeft = moveDistance
				//this.scrollSlide(moveDistance)
			} else if (this.deltaX > 0) {
				if (!this.slideWidth || !this.currentPage) {
					throw new Error('no slide found, cannot scroll slider')
				}
				const moveDistance =
					this.slider.scrollLeft + this.slideWidth + 5
				this.slider.scrollLeft = moveDistance
				//this.scrollSlide(moveDistance)
			}
		}

		document.addEventListener('mousemove', onMouseMove)
		document.addEventListener('mouseup', onMouseUp)
	}
}
